import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CustomItemModel, CustomItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['customItems']

type CustomItemFilters = {
  agency_id?: number,
  custom_item_group_id?: number,
  string?: string,
}

type UseCustomItemsOptions = {
  filters?: CustomItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: CustomItemRequestOptions,
}

function useCustomItems(options: UseCustomItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    custom_item_group_id: filterCustomItemGroupId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { customItems: customItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { customItems } = useSelector(reduxState => reduxState.entities)

  const filteredCustomItems = useMemo(() => {
    const filtered = Object.values(customItems).filter((customItem: CustomItemModel) => {
      const {
        agency_id, custom_item_group_id, id, title,
      } = customItem

      const matchAgency = matchFilterNumber(agency_id, filterAgencyId)
      const matchCustomItemGroup = matchFilterNumber(custom_item_group_id, filterCustomItemGroupId)
      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchAgency && matchCustomItemGroup && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [customItemsUpdatedAt, JSON.stringify(filters)])

  const filteredCustomItemsCount = filteredCustomItems.length
  const hasFilteredCustomItems = !!filteredCustomItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCustomItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCustomItems } = useReduxAction(
    'customItems',
    'loadCustomItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCustomItems,
    filteredCustomItemsCount,
    hasFilteredCustomItems,
    loading: loadingCustomItems,
  }
}

export default useCustomItems
